import { ErrorBody } from '../wire';

/**
 * What went wrong, in the closed vocabulary `guest-api` actually uses.
 *
 * The server's promise is "never a 200 with an error inside it" and one
 * serializer for every failure (`guest-api/src/http.ts`). This is the other end
 * of that: one failure type, carrying the server's own words, because on a
 * public booking page the words *are* the product. "We cannot send a code to
 * that number" is a sentence a guest can act on; "Request failed" is one that
 * makes them telephone the spa, which is the outcome §6.4.4 spends a whole
 * section avoiding.
 *
 * Two codes are ours rather than the server's, and neither can be confused with
 * one of its:
 *
 *  - `offline` — `fetch` rejected, so the request never completed. A phone in a
 *    lift, not a refusal.
 *  - `malformed` — a 2xx whose body this bundle could not parse. One static
 *    build serves every tenant hostname (§6.4.2), so this is what a bundle one
 *    release behind the API looks like from the inside, and it must be visible
 *    rather than swallowed into an empty screen.
 */

export type FailureCode = ErrorBody['error']['code'] | 'offline' | 'malformed';

export class ApiFailure extends Error {
  readonly code: FailureCode;
  readonly status: number;
  readonly detail: Record<string, unknown> | undefined;
  /** From the `Retry-After` header, which `guest-api` sets on `rate_limited`. */
  readonly retryAfterSeconds: number | undefined;

  constructor(
    code: FailureCode,
    message: string,
    options: {
      status?: number;
      detail?: Record<string, unknown>;
      retryAfterSeconds?: number;
    } = {},
  ) {
    super(message);
    this.name = 'ApiFailure';
    this.code = code;
    this.status = options.status ?? 0;
    this.detail = options.detail;
    this.retryAfterSeconds = options.retryAfterSeconds;
  }

  static offline(cause: string): ApiFailure {
    return new ApiFailure('offline', 'We could not reach the booking system.', {
      detail: { cause },
    });
  }
}

/**
 * The session ended while the page was open.
 *
 * `guest-api` answers `401 unauthenticated` for an expired cookie, a rotated
 * `SESSION_SECRET`, and a client row that has been anonymized under §7.6 — all
 * three with "Sign in again." The SPA treats them identically because the remedy
 * is identical: prove the identity once more. Nothing is lost by doing so; the
 * booking that was half-filled is still on the screen.
 */
export function isExpiredSession(failure: ApiFailure): boolean {
  return failure.code === 'unauthenticated';
}

/** Turn a non-2xx body into the failure it describes. */
export function failureFromBody(
  status: number,
  body: unknown,
  retryAfterSeconds?: number,
): ApiFailure {
  const parsed = ErrorBody.safeParse(body);
  if (parsed.success) {
    return new ApiFailure(parsed.data.error.code, parsed.data.error.message, {
      status,
      ...(parsed.data.error.detail === undefined ? {} : { detail: parsed.data.error.detail }),
      ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
    });
  }
  // The closed error set is `guest-api`'s promise, not the load balancer's. A
  // Cloud Armor block page or a 502 from the LB while a revision rolls still has
  // to become one of our codes, because the page only knows how to render ours.
  return new ApiFailure(
    status >= 500 ? 'unavailable' : status === 404 ? 'not_found' : 'invalid',
    status >= 500 ? 'The booking system is temporarily unavailable.' : `HTTP ${status}.`,
    { status },
  );
}

export function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

export function asFailure(e: unknown): ApiFailure {
  return e instanceof ApiFailure ? e : new ApiFailure('unavailable', messageOf(e));
}
